/**
 * Ripple — Material-style ink ripple from click point.
 *
 * Two exports:
 * - `useRipple(ref)` — attaches pointerdown listener, creates CSS-animated spans
 * - `<Ripple>` — convenience wrapper that adds relative + overflow-hidden
 *
 * Requires `.ripple-wave` keyframes in index.css.
 * No-op on touch devices (pointer: coarse) and prefers-reduced-motion.
 */

import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

const isCoarse =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches;

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useRipple(ref: RefObject<HTMLElement | null>): void {
  const disabled = useRef(isCoarse || prefersReduced);

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled.current) return;

    const handler = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2.5;
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      const span = document.createElement('span');
      span.className = 'ripple-wave';
      span.style.width = `${size}px`;
      span.style.height = `${size}px`;
      span.style.left = `${x}px`;
      span.style.top = `${y}px`;
      span.style.background = 'currentColor';
      el.appendChild(span);
      span.addEventListener('animationend', () => span.remove(), { once: true });
    };

    el.addEventListener('pointerdown', handler);
    return () => el.removeEventListener('pointerdown', handler);
  }, [ref]);
}

interface RippleProps {
  children: ReactNode;
  className?: string;
}

export function Ripple({ children, className = '' }: RippleProps) {
  const ref = useRef<HTMLDivElement>(null);
  useRipple(ref);

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      {children}
    </div>
  );
}
